import { Schema } from 'effect';

import type { RouteTask } from '../../route/catchAll/RouteTask';

export type QueryPair = readonly [name: string, value: string];

export const ListingUrl = Schema.Struct({
  pathname: Schema.String,
  query: Schema.Array(Schema.Tuple([Schema.String, Schema.String])),
});

export type ListingUrl = typeof ListingUrl.Type;

/** Converts a catch-all task into an ordered URL representation. */
export const fromTask = (task: RouteTask): ListingUrl => {
  const query: QueryPair[] = [];

  for (const [name, value] of Object.entries(task.query)) {
    if (typeof value === 'string') query.push([name, value]);
    if (Array.isArray(value)) {
      for (const item of value) query.push([name, item]);
    }
  }

  return { pathname: task.pathname, query };
};

/** Converts browser location fields into an ordered URL representation. */
export const fromLocation = (location: {
  readonly pathname: string;
  readonly search: string;
}): ListingUrl => ({
  pathname: location.pathname,
  query: Array.from(new URLSearchParams(location.search).entries()),
});

/** Converts an ordered URL representation into a relative href. */
export const href = (url: ListingUrl): string => {
  if (url.query.length === 0) return url.pathname;

  const query = url.query
    .map(([name, value]) => `${encodeURIComponent(name)}=${encodeURIComponent(value)}`)
    .join('&');

  return `${url.pathname}?${query}`;
};

/** Compares pathnames and ordered query pairs. */
export const equalsUrl = (left: ListingUrl, right: ListingUrl): boolean =>
  left.pathname === right.pathname &&
  left.query.length === right.query.length &&
  left.query.every(
    ([name, value], index) => right.query[index]?.[0] === name && right.query[index]?.[1] === value
  );
